// Карточка сегмента на вертикальном таймлайне варианта.
import { useState, type CSSProperties, type DragEvent, type KeyboardEvent } from 'react';
import type { Currency, CurrencyDef, Segment, VariantTotals } from '../../types';
import {
  flightDurationMin,
  formatDuration,
  formatLocal,
  formatMoney,
  pluralNights,
  segmentAccent,
  wholeStayNights,
} from '../../core';
import { useActions, useStore } from '../../store';
import { useT, type Translate } from '../../i18n';
import { EditIcon, KindIcon, SEGMENT_DND_MIME } from '../library';
import { SegmentFormModal } from '../forms';
import { useDnd, useTouchDrag, resolveDrop, type DragPayload } from '../dnd';
import { PriceBadge, PriceHistoryModal, PriceRefreshButton } from '../../prices';
import styles from './TimelineCard.module.css';

export interface TimelineCardProps {
  segment: Segment;
  variantId: string;
  index: number;
  totalCount: number;
  totals: VariantTotals;
  /** вариант защищён замком: карточку нельзя таскать, править и убирать */
  locked?: boolean;
}

function WarnIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <line x1="12" y1="7" x2="12" y2="14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="12" cy="17.2" r="0.6" fill="currentColor" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

/** Крестик — «убрать из варианта», а не «удалить»: блок остаётся в библиотеке.
 *  Поэтому он и не уехал в общий набор — там корзина со своим смыслом. */
function RemoveIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function SegmentBody({
  segment,
  priceMinor,
  currency,
  currencies,
  t,
  onOpenHistory,
}: {
  segment: Segment;
  priceMinor: number;
  currency: Currency;
  currencies: readonly CurrencyDef[];
  t: Translate;
  /** Открыть журнал проверок цены этого рейса — есть только у flight. */
  onOpenHistory: () => void;
}) {
  if (segment.kind === 'flight') {
    const dur = flightDurationMin(segment);
    const showPairNote = segment.priceScope === 'round_trip_full' && priceMinor === 0;
    return (
      <>
        <div className={styles.row1}>
          <span className={`${styles.iataTitle} iata`}>
            {segment.from} → {segment.to}
          </span>
          {showPairNote ? (
            <span className={styles.pairNote}>{t('timeline.pairPrice')}</span>
          ) : (
            <span className={`${styles.price} mono`}>{formatMoney(priceMinor, currency, currencies)}</span>
          )}
        </div>
        <div className={`${styles.times} mono`}>
          {formatLocal(segment.departLocal)} → {formatLocal(segment.arriveLocal)}
          <span className={styles.dur}>{dur !== null ? formatDuration(dur, t) : '—'}</span>
        </div>
        {/* Что показывает агрегатор сейчас — отдельной строкой под временем:
            в строку с ним бейдж не влезает, а время многоточием не обрезать.
            Пока проверка цен недоступна, PriceBadge отдаёт null и строка
            схлопывается по :empty. */}
        <div className={styles.priceLine}>
          <PriceBadge segment={segment} onOpenHistory={onOpenHistory} />
        </div>
        <div className={styles.subtitle}>
          {segment.carrier}
          {segment.flightNumber ? ` ${segment.flightNumber}` : ''}
          {/* Ссылка на рейс или билет — единственный способ вернуться к
              источнику цены. rel обязателен: страница открывается в новой
              вкладке и не должна получить доступ к этой через window.opener. */}
          {segment.url && (
            <a
              className={styles.sourceLink}
              href={segment.url}
              target="_blank"
              rel="noopener noreferrer"
              title={segment.url}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {t('field.url.short')}
            </a>
          )}
        </div>
      </>
    );
  }

  if (segment.kind === 'lodging') {
    return (
      <div className={styles.row1}>
        <span className={styles.title}>{segment.city}</span>
        <span className={`${styles.price} mono`}>{formatMoney(priceMinor, currency, currencies)}</span>
      </div>
    );
  }

  if (segment.kind === 'transfer') {
    return (
      <>
        <div className={styles.row1}>
          <span className={styles.title}>
            {segment.from} → {segment.to}
          </span>
          <span className={`${styles.price} mono`}>{formatMoney(priceMinor, currency, currencies)}</span>
        </div>
        <div className={`${styles.subtitle} mono`}>{formatDuration(segment.durationMin, t)}</div>
      </>
    );
  }

  // taxi / food / misc / baggage
  const qty = segment.kind === 'baggage' ? 1 : segment.quantity;
  return (
    <div className={styles.row1}>
      <span className={styles.title}>
        {segment.label}
        {qty > 1 ? ` ×${qty}` : ''}
      </span>
      <span className={`${styles.price} mono`}>{formatMoney(priceMinor, currency, currencies)}</span>
    </div>
  );
}

export function TimelineCard({
  segment,
  variantId,
  index,
  totalCount,
  totals,
  locked = false,
}: TimelineCardProps) {
  const state = useStore();
  const actions = useActions();
  const dnd = useDnd();
  const t = useT();
  const [editing, setEditing] = useState(false);
  // Журнал проверок открывается по бейджу цены. Держим id, а не флаг: модал
  // принимает segmentId и по нему же понимает, что он закрыт.
  const [historyFor, setHistoryFor] = useState<string | null>(null);

  const priceMinor = totals.perSegmentMinor[segment.id] ?? 0;
  const warnings = totals.warnings.filter((w) => w.segmentId === segment.id);
  // «На весь промежуток» ночи зависят от места блока в этом варианте: подпись
  // показывает посчитанное число — то же, что в итогах (calc: wholeStayNights).
  let nightsText: string | null = null;
  if (segment.kind === 'lodging') {
    if (segment.wholeStay) {
      const segIds = state.variants.find((v) => v.id === variantId)?.segmentIds ?? [];
      const computed = wholeStayNights(segment.id, segIds, state) ?? segment.nights;
      nightsText = `${t('labels.wholeStay')} · ${pluralNights(computed, t)}`;
    } else {
      nightsText = pluralNights(segment.nights, t);
    }
  }

  const payload: DragPayload = { segmentId: segment.id, fromVariantId: variantId, fromIndex: index };

  const { handlers, lifted } = useTouchDrag({
    payload,
    onDrop: (target) => resolveDrop(actions, payload, target),
  });

  // Тач-перетаскивание вешает свои обработчики на карточку — под замком их не вешаем вовсе.
  const dragHandlers = locked ? {} : handlers;

  function handleDragStart(e: DragEvent<HTMLDivElement>) {
    e.dataTransfer.setData(SEGMENT_DND_MIME, segment.id);
    e.dataTransfer.setData('text/plain', segment.id);
    e.dataTransfer.effectAllowed = 'copyMove';
  }

  function handleDragEnd() {
    dnd.end();
  }

  function moveBy(delta: number) {
    if (locked) return;
    const to = index + delta;
    if (to < 0 || to >= totalCount) return;
    actions.moveInVariant(variantId, index, to);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (locked) return;
    if (e.altKey && e.key === 'ArrowUp') {
      e.preventDefault();
      moveBy(-1);
    } else if (e.altKey && e.key === 'ArrowDown') {
      e.preventDefault();
      moveBy(1);
    }
  }

  // Проверка цены — такая же иконка в ряду действий, как правка и удаление.
  // Размер compact-кнопки (24×24, на телефоне 38×32) задаёт себе сам модуль
  // цен — тот же, что у .iconBtn ниже; отсюда уезжают только отступы и кегль,
  // иначе два одноклассовых правила спорили бы за размер по порядку в бандле.
  const priceRefresh =
    segment.kind === 'flight' ? (
      <PriceRefreshButton segments={[segment]} compact className={styles.actionBtn} />
    ) : null;

  return (
    <>
      <div
        className={`${styles.card} ${lifted ? styles.lifted : ''}`}
        style={{ '--accent': `var(--${segmentAccent(segment.kind)})` } as CSSProperties}
        data-segment-id={segment.id}
        data-drop-index={index}
        tabIndex={0}
        draggable={!locked}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onKeyDown={handleKeyDown}
        {...dragHandlers}
      >
        <span className={styles.marker} aria-hidden="true">
          <KindIcon kind={segment.kind} size={13} />
        </span>
        <div className={styles.body}>
          <SegmentBody
            segment={segment}
            priceMinor={priceMinor}
            currency={totals.currency}
            currencies={state.currencies}
            t={t}
            onOpenHistory={() => setHistoryFor(segment.id)}
          />
          {nightsText && <div className={styles.subtitle}>{nightsText}</div>}
          {warnings.length > 0 && (
            <ul className={styles.warnings}>
              {warnings.map((w, i) => (
                <li key={i} className={`${styles.badge} ${w.level === 'error' ? styles.error : styles.warn}`}>
                  <WarnIcon />
                  <span>{w.message}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {locked ? (
          /* Под замком остаётся ровно одно действие — проверить цену: она
             живёт в истории проверок, а не в поездке, и вариант не меняет.
             Поставить найденную цену в блок замок по-прежнему не даёт (гард
             в reducer, isSegmentLocked). */
          <div className={styles.actions}>
            <span className={styles.lockedNote} title={t('timeline.lockedNote.tip')}>
              {t('timeline.lockedNote')}
            </span>
            {priceRefresh}
          </div>
        ) : (
          /* Только иконки: подписи «Изменить»/«Убрать» съедали карточку, а
             смысл и так читается; название остаётся в title и aria-label. */
          <div className={styles.actions}>
            {priceRefresh}
            <button
              type="button"
              className={`btn btn-sm ${styles.actionBtn} ${styles.iconBtn}`}
              aria-label={t('library.edit')}
              title={t('library.edit')}
              onClick={() => setEditing(true)}
            >
              <EditIcon size={12} />
            </button>
            <button
              type="button"
              className={`btn btn-sm btn-danger ${styles.actionBtn} ${styles.iconBtn}`}
              aria-label={t('timeline.remove')}
              title={t('timeline.remove')}
              onClick={() => actions.removeFromVariant(variantId, segment.id)}
            >
              <RemoveIcon />
            </button>
            {/* Вверх/вниз: на десктопе — узкий столбик шириной в один квадратик
                (кнопки друг на друге), на мобилке — большие, в строку. */}
            <div className={styles.mover}>
              <button
                type="button"
                className={`btn btn-sm ${styles.actionBtn} ${styles.moveBtn}`}
                aria-label={t('timeline.moveUp')}
                title={t('timeline.moveUp')}
                disabled={index === 0}
                onClick={() => moveBy(-1)}
              >
                ↑
              </button>
              <button
                type="button"
                className={`btn btn-sm ${styles.actionBtn} ${styles.moveBtn}`}
                aria-label={t('timeline.moveDown')}
                title={t('timeline.moveDown')}
                disabled={index === totalCount - 1}
                onClick={() => moveBy(1)}
              >
                ↓
              </button>
            </div>
          </div>
        )}
      </div>
      <SegmentFormModal open={editing} onClose={() => setEditing(false)} mode="edit" segmentId={segment.id} />
      <PriceHistoryModal segmentId={historyFor} onClose={() => setHistoryFor(null)} />
    </>
  );
}
